import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const JOURS_ORDER = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const JS_DAY_TO_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];

export default function Menus() {
  const [plats, setPlats] = useState([]);
  const [formules, setFormules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [p, f] = await Promise.all([api.get("/plats-jour"), api.get("/formules")]);
      setPlats(p.data);
      setFormules(f.data);
    } catch (e) {
      setErr("Impossible de charger les menus.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saison = formules.find((f) => f.type === "saison");
  const todayFr = JS_DAY_TO_FR[new Date().getDay()];

  const groupedPlats = JOURS_ORDER
    .map((j) => ({ jour: j, items: plats.filter((p) => p.jour === j) }))
    .filter((g) => g.items.length > 0);

  return (
    <div data-testid="menus-page" className="bg-[#FDFBF7]">
      <PageHero
        title="Nos menus"
        subtitle="Des plats du jour, des formules saisonnières, et une carte qui évolue au fil du marché."
        imageUrl="/images/food.jpeg"
      />

      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-20 pb-28">
        {loading && <p className="text-center text-[#1A1A1A]/60">Chargement…</p>}
        {err && <p className="text-center text-[#A61C24]">{err}</p>}

        {/* FORMULES */}
        <section className="mb-24">
          <div className="text-center mb-14">
            <span className="font-script text-2xl text-[#A61C24]">Nos formules</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">Express & du Bistrot</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-0 border border-[#E5E0D8] bg-white divide-y md:divide-y-0 md:divide-x divide-[#E5E0D8]">
            <FormuleExpressCard />
            {saison && <FormuleCard f={saison} />}
          </div>
        </section>

        {/* PLATS DU JOUR */}
        <section>
          <div className="text-center mb-14">
            <span className="font-script text-2xl text-[#A61C24]">Cette semaine</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-2">Les plats du jour</h2>
          </div>
          {groupedPlats.length === 0 && !loading && (
            <p className="text-center text-[#1A1A1A]/60">Aucun plat du jour publié pour le moment.</p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {groupedPlats.map((g) => {
              const isToday = g.jour === todayFr;
              return (
                <div
                  key={g.jour}
                  data-testid={`plat-jour-${g.jour}`}
                  className={`relative p-8 flex flex-col bg-white border transition-colors duration-300 ${
                    isToday
                      ? "border-[#A61C24]"
                      : "border-[#E5E0D8] hover:border-[#A61C24]/40"
                  }`}
                >
                  {isToday && (
                    <span className="absolute -top-3 left-8 px-3 py-1 bg-[#A61C24] text-white text-[10px] uppercase tracking-[0.25em]">
                      Aujourd'hui
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs uppercase tracking-[0.3em] text-[#A61C24]">
                      {g.jour}
                    </span>
                    <span className="flex-1 h-px bg-[#E5E0D8]" />
                  </div>
                  <div className="space-y-5 flex-1">
                    {g.items.map((item) => (
                      <div key={item.id} className="space-y-1.5">
                        <h3 className="font-serif text-2xl leading-tight text-[#1A1A1A]">
                          {item.nom}
                        </h3>
                        {item.description && (
                          <p className="text-sm italic leading-relaxed text-[#1A1A1A]/65">
                            {item.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* OUTRO CARTE SUR PLACE */}
        <section className="mt-28 text-center max-w-3xl mx-auto" data-testid="menus-outro">
          <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
            Venez découvrir <em className="text-[#A61C24]">notre carte</em> sur place…
          </h2>
          <p className="text-[#1A1A1A]/70 leading-relaxed mt-4">
            Retrouvez ci-dessous nos formules et nos plats du jour de la semaine, renouvelés au gré du marché et des saisons.
          </p>
        </section>
      </div>
    </div>
  );
}

function FormuleExpressCard() {
  return (
    <div className="p-10 text-center" data-testid="formule-express">
      <h3 className="font-serif text-3xl mb-4">Formule Express</h3>
      <p className="text-[#1A1A1A]/75 leading-relaxed mb-6 italic">
        Le plat du jour et l'Express sont servis uniquement le midi<br />
        (sauf le samedi, les jours fériés et le mois d'août).
      </p>
      <div className="w-12 h-px bg-[#A61C24] my-6 mx-auto" />
      <div className="space-y-4 text-[#1A1A1A]/85 leading-relaxed">
        <p>Entrée du jour <span className="text-[#A61C24]">+</span> plat du jour</p>
        <p className="font-script text-2xl text-[#A61C24]">ou</p>
        <p>Plat du jour <span className="text-[#A61C24]">+</span> dessert du jour</p>
        <p className="font-script text-2xl text-[#A61C24]">ou</p>
        <p>Entrée du jour <span className="text-[#A61C24]">+</span> plat du jour <span className="text-[#A61C24]">+</span> dessert du jour</p>
      </div>
    </div>
  );
}

function FormuleCard({ f }) {
  return (
    <div className="p-10 text-center" data-testid={`formule-${f.type}`}>
      <h3 className="font-serif text-3xl mb-4">{f.nom}</h3>
      {f.description && <p className="text-[#1A1A1A]/75 leading-relaxed mb-6 whitespace-pre-line">{f.description}</p>}
      <div className="w-12 h-px bg-[#A61C24] my-6 mx-auto" />
      <div className="space-y-5 text-sm">
        {f.entrees?.length > 0 && <Section label="Entrées" items={f.entrees} />}
        {f.plats?.length > 0 && <Section label="Plats" items={f.plats} />}
        {f.desserts?.length > 0 && <Section label="Desserts" items={f.desserts} />}
      </div>
    </div>
  );
}

function Section({ label, items }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-[0.25em] text-[#A61C24] block">{label}</span>
      <ul className="mt-2 text-[#1A1A1A]/85">
        {items.map((it, i) => (
          <li key={i}>
            {i > 0 && (
              <div className="flex items-center justify-center gap-2 my-2" aria-hidden>
                <span className="w-6 h-px bg-[#E5E0D8]" />
                <span className="font-script text-base text-[#A61C24]/80">ou</span>
                <span className="w-6 h-px bg-[#E5E0D8]" />
              </div>
            )}
            <span className="block">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PageHero({ title, subtitle, imageUrl }) {
  return (
    <section className="relative h-[44vh] min-h-[300px] overflow-hidden flex items-end">
      <div className="absolute inset-0 bg-cover bg-center kenburns" style={{ backgroundImage: `url('${imageUrl}')` }} aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" aria-hidden />
      <div className="relative z-10 max-w-6xl mx-auto w-full px-6 md:px-10 pb-14 text-white">
        <h1 className="font-serif text-5xl md:text-6xl mb-3">{title}</h1>
        <p className="text-white/80 max-w-xl">{subtitle}</p>
      </div>
    </section>
  );
}
